import { Fragment, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import PartyAddressConfirmPanel from './PartyAddressConfirmPanel'
import { supabase } from '../api/supabase'
import { useToast } from '../hooks/useToast'
import type { BoxtopMatchCandidate } from '../hooks/useSyncToBoxtrax'
import type { InvoiceToAddressSide, InvoiceToMismatch } from './InvoiceToVerifyDialog'
import { StatusBadge } from './InvoiceToVerifyDialog'
import BoxtopAccountPanel from './BoxtopAccountPanel'
import BoxtopAddressLinks, { addressLinksReady, type AddressLinkView } from './BoxtopAddressLinks'
import { callAccountAction, type Step1Data } from '../lib/boxtopAccount'

// Z58.7 (KI26048): consolidated "Sync Preflight" modal -- the preferred UX from Z58.6, now
// required because the old two-stage sequential flow (Stage 1 InvoiceTo dialog, THEN Stage 2
// BoxtopContactMatchDialog only when a party was blocked) had no way to show a party that never
// gets blocked at all -- Consignee/Notify being the SAME customer as InvoiceTo (e.g. both BNJ)
// already carry InvoiceTo's boxtop_account_number, so the old preflightContactMatch's
// needsMatch filter silently excluded them and nothing was ever shown for them. Every party now
// gets a real, always-visible status here -- 'parent_match'/'auto_matched' are passive green
// cards (no click required), 'needs_decision' blocks "Approve All & Sync".
//
// Z58.9: 'needs_relink' added -- an already-set boxtop_account_number that ISN'T the same as
// InvoiceTo's own was previously shown as a green "Already linked" verified check with no actual
// check behind it (see computeSyncPreflight). That's exactly how LFFL9107/SRIC4853/
// CONS-1781214332891 -- our own wrongly-created standalone shell Contacts -- ended up displayed
// as verified. It's non-blocking (doesn't stop "Approve All & Sync") since we can't yet tell a
// real independent account from a wrongly-created one, but staff can re-link it inline via
// BoxtopCodeEntry.

type PartyStatus = 'parent_match' | 'auto_matched' | 'needs_decision' | 'needs_relink' | 'own_account'

interface PartyPreflightStatus {
  role: string
  customerId: string
  customerName: string
  customerAddress?: string
  // hbl = found under the Customer's BoxTop account; mbl = independent of the Customer, found under its OWN BoxTop code only
  level?: 'hbl' | 'mbl' | 'plain'
  addressRole?: string          // the role the job address is stored under (hbl_consignor ...), from the server
  sameCompany?: boolean         // this party IS the Customer's company, not only filed under the Customer's BoxTop account
  status: PartyStatus
  boxtopAccountNumber?: string
  parentAccNum?: string
  matchedName?: string
  matchScorePct?: number
  matchSource?: 'standalone' | 'parent_sub_address'
  candidates?: BoxtopMatchCandidate[]
}

const MATCH_SOURCE_LABELS: Record<string, string> = {
  standalone: 'standalone Contact',
  parent_sub_address: "existing sub-address under the parent",
}

interface PreflightData {
  invoiceTo: InvoiceToMismatch
  parties: PartyPreflightStatus[]
  addressLinks?: AddressLinkView[]
}

// customer_account_only: an MBL party with no code of its own -- nothing is saved on the company; its address is searched in the Customer's
// account (round 2) and must be confirmed in 'Addresses in BoxTop' below.
type Decision = { type: 'use'; accountNumber: string } | { type: 'create_new' } | { type: 'customer_account_only' }

const ROLE_LABELS: Record<string, string> = {
  consignor: 'Shipper',
  importer: 'Consignee',
  notify: 'Notify Party',
  agent: 'Overseas Agent',
}

interface Props {
  jobRef: string
  onApproved: () => void
  onCancel: () => void
}

function AddressPanel({ title, address }: { title: string; address: InvoiceToAddressSide | null }) {
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 space-y-1 text-sm">
      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{title}</p>
      {address ? (
        <>
          {address.address_lines.map((l, i) => <p key={i} className="text-gray-900 dark:text-white">{l}</p>)}
          <p className="text-gray-900 dark:text-white">{[address.city, address.postcode].filter(Boolean).join(', ') || '—'}</p>
          <p className="text-gray-500 dark:text-gray-400">{address.country || '—'}</p>
        </>
      ) : (
        <p className="text-red-600 dark:text-red-400">Not found on BoxTop — this account number doesn't resolve to a real Contact.</p>
      )}
    </div>
  )
}

function PartyBadge({ status, candidateCount, sameCompany }: { status: PartyStatus; candidateCount: number; sameCompany?: boolean }) {
  if (status === 'needs_decision') {
    return candidateCount === 0
      ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"><span>✗</span>No match found</span>
      : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400"><span>⚠</span>Needs decision</span>
  }
  if (status === 'needs_relink') {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400"><span>⚠</span>Needs re-linking</span>
  }
  // Say what was actually established. "Verified" / "Same as" claimed more than the check did.
  const [text, tone] =
    status === 'parent_match' ? (sameCompany ? ['Same company as the Customer', 'green'] : ["Filed under the Customer's account", 'blue'])
    : status === 'own_account' ? ['Has its own BoxTop account', 'green']
    : ['Matched by name', 'green']
  const cls = tone === 'blue' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}>{text}</span>
}

// Z58.9 (Z58.8 follow-through): staff hit a wrongly-bound/unresolvable BoxTop account number
// repeatedly (LANDR's own LFFL9107 shell, plus Shipper/Consignee ending up as standalone
// Contacts) with no way to correct it except leaving this modal for the Customer page. One
// inline free-text entry, reused for InvoiceTo's own account and for any party needing a
// decision or re-link.
function BoxtopCodeEntry({ label, onApply, disabled }: { label: string; onApply: (code: string) => void; disabled?: boolean }) {
  const [open, setOpen] = useState(false)
  const [value, setValue] = useState('')

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} disabled={disabled} className="text-xs text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50">
        {label}
      </button>
    )
  }
  return (
    <div className="flex items-center gap-2">
      <input
        value={value}
        onChange={e => setValue(e.target.value.toUpperCase())}
        placeholder="e.g. LANDR"
        maxLength={8}
        disabled={disabled}
        className="text-sm font-mono border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-2 py-1 w-28"
      />
      <button
        type="button"
        disabled={disabled || !value.trim()}
        onClick={() => { onApply(value.trim()); setOpen(false); setValue('') }}
        className="px-2 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium disabled:opacity-50"
      >
        Use this code
      </button>
      <button type="button" disabled={disabled} onClick={() => { setOpen(false); setValue('') }} className="text-xs text-gray-500 dark:text-gray-400 hover:underline disabled:opacity-50">
        Cancel
      </button>
    </div>
  )
}

export default function SyncPreflightModal({ jobRef, onApproved, onCancel }: Props) {
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [preflight, setPreflight] = useState<PreflightData | null>(null)
  // Every party's address recorded on the job (reported by PartyAddressConfirmPanel). Approve stays off until it is.
  const [addressesRecorded, setAddressesRecorded] = useState(false)
  // Z102 PR1: Step 1 (which BoxTop account this job's customer maps to, and does it resolve). A failure
  // here means `preflight` stays null -- nothing below it is shown or approvable.
  const [step1, setStep1] = useState<Step1Data | null>(null)
  const { toast } = useToast()

  // InvoiceTo (Section 1) state -- adapted inline from InvoiceToVerifyDialog's resolve actions,
  // since this modal owns the whole flow instead of a separate popup.
  const [invoiceToResolved, setInvoiceToResolved] = useState(false)
  const [invoiceToSaving, setInvoiceToSaving] = useState<'boxtop' | 'ours' | 'edit' | null>(null)
  const [invoiceToError, setInvoiceToError] = useState<string | null>(null)
  const [editingInvoiceTo, setEditingInvoiceTo] = useState(false)
  const [editLines, setEditLines] = useState('')
  const [editCity, setEditCity] = useState('')
  const [editPostcode, setEditPostcode] = useState('')

  // Sections 2-4 (Shipper/Consignee/Notify) -- same decision shape as the old
  // BoxtopContactMatchDialog, now one per party inline instead of a separate dialog.
  const [decisions, setDecisions] = useState<Record<string, Decision>>({})
  const [approving, setApproving] = useState(false)
  const [approveError, setApproveError] = useState<string | null>(null)

  const loadPreflight = async (cancelledRef?: { current: boolean }) => {
    setLoading(true); setLoadError(null)
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const accessToken = sessionData?.session?.access_token || ''
      const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sync-to-boxtrax`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ action: 'sync_preflight', job_ref: jobRef }),
      })
      const result = await resp.json()
      console.log('[SyncPreflightModal] preflight response:', result)
      if (cancelledRef?.current) return
      setStep1(result.step1 ?? null)
      if (!result.success) {
        setPreflight(null)
        // A Step 1 failure is shown as its own card (with the real reason and a way to fix it), not as a generic error.
        if (!result.step1) setLoadError(result.error || 'Failed to load Sync Preflight')
        return
      }
      setPreflight({ invoiceTo: result.invoiceTo, parties: result.parties || [], addressLinks: result.addressLinks || [] })
      setInvoiceToResolved(result.invoiceTo?.status === 'match')
      setEditLines(result.invoiceTo?.ours?.address_lines?.join('\n') || '')
      setEditCity(result.invoiceTo?.ours?.city || '')
      setEditPostcode(result.invoiceTo?.ours?.postcode || '')
    } catch (err: any) {
      if (!cancelledRef?.current) setLoadError(err?.message || 'Failed to load Sync Preflight')
    } finally {
      if (!cancelledRef?.current) setLoading(false)
    }
  }

  useEffect(() => {
    console.log('[SyncPreflightModal] mounted -- jobRef:', jobRef)
    const cancelledRef = { current: false }
    loadPreflight(cancelledRef)
    return () => {
      cancelledRef.current = true
      console.log('[SyncPreflightModal] unmounted -- jobRef:', jobRef)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobRef])

  const logInvoiceToVerification = async (action: 'use_boxtop' | 'edit_both', after: InvoiceToAddressSide) => {
    if (!preflight) return
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      await supabase.from('customer_address_verifications').insert({
        customer_id: preflight.invoiceTo.invoiceToCustomerId,
        verified_by: sessionData?.session?.user?.email || null,
        action_taken: action,
        before_state: { ours: preflight.invoiceTo.ours, boxtop: preflight.invoiceTo.boxtop },
        after_state: after,
      })
    } catch (e) {
      console.warn('[SyncPreflightModal] Failed to log InvoiceTo verification (non-blocking):', e)
    }
  }

  const writeInvoiceToAddress = async (address: InvoiceToAddressSide) => {
    if (!preflight) return
    const { error: err } = await supabase
      .from('customer_addresses')
      .update({
        address_lines: address.address_lines,
        city: address.city,
        postcode: address.postcode,
        country: address.country,
        address_manually_verified: true,
      })
      .eq('customer_id', preflight.invoiceTo.invoiceToCustomerId)
      .eq('address_type', 'operations')
    if (err) throw err
  }

  const useInvoiceToBoxtop = async () => {
    if (!preflight?.invoiceTo.boxtop) return
    setInvoiceToSaving('boxtop'); setInvoiceToError(null)
    try {
      await writeInvoiceToAddress(preflight.invoiceTo.boxtop)
      await logInvoiceToVerification('use_boxtop', preflight.invoiceTo.boxtop)
      toast(`Updated ${preflight.invoiceTo.invoiceToCustomerName}'s address from BoxTop`, 'success')
      setInvoiceToResolved(true)
    } catch (err: any) {
      setInvoiceToError(err?.message || 'Failed to update our address')
    } finally {
      setInvoiceToSaving(null)
    }
  }

  const saveEditedInvoiceTo = async () => {
    if (!preflight) return
    setInvoiceToSaving('edit'); setInvoiceToError(null)
    try {
      const edited: InvoiceToAddressSide = {
        address_lines: editLines.split('\n').map(l => l.trim()).filter(Boolean),
        city: editCity.trim() || null,
        postcode: editPostcode.trim() || null,
        country: preflight.invoiceTo.ours.country,
      }
      await writeInvoiceToAddress(edited)
      await logInvoiceToVerification('edit_both', edited)
      toast(`Saved edited address for ${preflight.invoiceTo.invoiceToCustomerName} (ours only; BoxTop is not changed)`, 'success')
      setInvoiceToResolved(true)
      setEditingInvoiceTo(false)
    } catch (err: any) {
      setInvoiceToError(err?.message || 'Failed to save edited address')
    } finally {
      setInvoiceToSaving(null)
    }
  }

  const needsDecisionParties = (preflight?.parties || []).filter(p => p.status === 'needs_decision')
  // Z58.9: 'needs_relink' doesn't block approval (see PartyStatus comment above) but an
  // applied decision for one -- e.g. staff re-linking SRIC4853 -> LANDR -- still needs saving.
  const applicableParties = (preflight?.parties || []).filter(p => p.status === 'needs_decision' || p.status === 'needs_relink')
  const allDecided = needsDecisionParties.every(p => decisions[p.customerId])
  const addressLinksOk = addressLinksReady(preflight?.addressLinks)
  const allApproved = invoiceToResolved && allDecided && addressesRecorded && addressLinksOk
  console.log('[SyncPreflightModal] render -- invoiceToResolved:', invoiceToResolved, 'decisions:', decisions, 'allDecided:', allDecided, 'allApproved:', allApproved)

  const approve = async () => {
    if (!preflight) return
    setApproving(true); setApproveError(null)
    try {
      const now = new Date().toISOString()
      for (const p of applicableParties) {
        const decision = decisions[p.customerId]
        if (!decision) continue
        if (decision.type === 'use') {
          // Z102 PR1: a code is only saved after BoxTop confirms it (GetCustomerDetails 200), checked server
          // side. This is an explicit staff decision in this dialog, so replacing an existing code is
          // pre-confirmed here; an account that does not resolve still fails. Company-name differences are
          // advisory and never block (a party filed under its parent's account is EXPECTED to differ), and are
          // deliberately NOT recorded as "confirmed same company" here -- that is for the Step 1 card only.
          const r = await callAccountAction('bind_boxtop_account', {
            customer_id: p.customerId, account_number: decision.accountNumber, confirm_overwrite: true,
          })
          if (!r.success) throw new Error(`${p.customerName}: ${r.error || 'could not save the BoxTop account'}`)
        }
        const { error: err } = await supabase.from('customers').update({ boxtop_contact_reviewed_at: now }).eq('id', p.customerId)
        if (err) throw err
      }
      onApproved()
    } catch (err: any) {
      setApproveError(err?.message || err?.error_description || String(err) || 'Failed to save decisions')
    } finally {
      setApproving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white dark:bg-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Sync Preflight</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Every party is checked against BoxTop before syncing — review each below, then approve.
          </p>
        </div>

        <div className="p-6 space-y-5">
          {preflight && <PartyAddressConfirmPanel jobRef={jobRef} parties={preflight.parties} onStatus={setAddressesRecorded} />}
          {loading && <p className="text-sm text-gray-500 dark:text-gray-400">Loading preflight…</p>}
          {loadError && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg text-sm">{loadError}</div>
          )}

          {step1 && (
            <section className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-2">
                <p className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wide">Step 1</p>
                <p className="text-sm font-medium text-gray-900 dark:text-white">Confirm the customer and the BoxTop account to search</p>
              </div>
              <BoxtopAccountPanel
                customerId={step1.customerId}
                customerName={step1.customerName}
                customerCode={step1.customerCode}
                accountNumber={step1.accountNumber}
                saved={step1.saved}
                lastCheck={step1.check}
                nameConfirmed={step1.nameConfirmed}
                lastChange={step1.source.lastChange}
                onChanged={() => loadPreflight()}
              />
              {!step1.check.ok && (
                <p className="text-xs text-amber-600 dark:text-amber-400">Nothing below is checked, and nothing can be approved, until this account resolves.</p>
              )}
            </section>
          )}

          {preflight && (
            <>
              {/* Section 1: Customer (jobs.invoice_to_id) */}
              <section className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <p className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wide">Section 1</p>
                  <StatusBadge status={preflight.invoiceTo.status} />
                </div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  Customer — {preflight.invoiceTo.invoiceToCustomerName} (BoxTop account {preflight.invoiceTo.parentAccNum})
                </p>
                {invoiceToError && <div className="p-2 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg text-xs">{invoiceToError}</div>}
                {preflight.invoiceTo.note && <p className="text-xs text-amber-600 dark:text-amber-400">{preflight.invoiceTo.note}</p>}

                {invoiceToResolved ? (
                  <p className="text-xs text-green-700 dark:text-green-400">✓ Verified — matches BoxTop's record for this account.</p>
                ) : !editingInvoiceTo ? (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <AddressPanel title="Our records" address={preflight.invoiceTo.ours} />
                      <AddressPanel title="BoxTop" address={preflight.invoiceTo.boxtop} />
                    </div>
                    {!preflight.invoiceTo.boxtop && (
                      <div className="space-y-1">
                        <p className="text-xs text-red-600 dark:text-red-400">
                          BoxTop account {preflight.invoiceTo.parentAccNum} doesn't resolve to a real Contact — this may be one of our own wrongly-created placeholder codes (like LFFL9107 was for LANDR). Enter the real BoxTop account number for {preflight.invoiceTo.invoiceToCustomerName} below.
                        </p>
                        <p className="text-xs text-gray-600 dark:text-gray-300">To change the BoxTop account, use the Step 1 card above (it is checked with BoxTop before it can be saved).</p>
                      </div>
                    )}
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">Resolve Mismatch:</p>
                    <div className="flex gap-2">
                      <button onClick={useInvoiceToBoxtop} disabled={invoiceToSaving !== null || !preflight.invoiceTo.boxtop} className="flex-1 px-3 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium disabled:opacity-50">
                        {invoiceToSaving === 'boxtop' ? 'Saving…' : 'Use BoxTop → update ours'}
                      </button>
                      <button onClick={() => setEditingInvoiceTo(true)} disabled={invoiceToSaving !== null} className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
                        Edit ours
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="space-y-3">
                    <label className="block">
                      <span className="text-xs text-gray-600 dark:text-gray-400">Address lines (one per line)</span>
                      <textarea value={editLines} onChange={e => setEditLines(e.target.value)} rows={3} className="mt-1 w-full text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-2 py-1.5" />
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <label className="block">
                        <span className="text-xs text-gray-600 dark:text-gray-400">City</span>
                        <input value={editCity} onChange={e => setEditCity(e.target.value)} className="mt-1 w-full text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-2 py-1.5" />
                      </label>
                      <label className="block">
                        <span className="text-xs text-gray-600 dark:text-gray-400">Postcode</span>
                        <input value={editPostcode} onChange={e => setEditPostcode(e.target.value)} className="mt-1 w-full text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-2 py-1.5" />
                      </label>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => setEditingInvoiceTo(false)} disabled={invoiceToSaving !== null} className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
                        Back
                      </button>
                      <button onClick={saveEditedInvoiceTo} disabled={invoiceToSaving !== null} className="flex-1 px-3 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium disabled:opacity-50">
                        {invoiceToSaving === 'edit' ? 'Saving…' : 'Save our address'}
                      </button>
                    </div>
                  </div>
                )}
              </section>

              {/* Sections 2-4: Shipper / Consignee / Notify */}
              {preflight.parties.map((party, idx) => {
                const decision = decisions[party.customerId]
                const prevLevel = idx > 0 ? preflight.parties[idx - 1].level : undefined
                const levelHeading = party.level === 'mbl' && prevLevel !== 'mbl'
                  ? 'MBL parties — independent of the customer; each is looked up under its OWN BoxTop code'
                  : party.level !== 'mbl' && (idx === 0 || prevLevel === 'mbl')
                    ? `HBL / customer parties — looked up under the Customer's BoxTop code${step1?.accountNumber ? ` (${step1.accountNumber})` : ''}`
                    : null
                return (
                  <Fragment key={party.customerId + party.role}>
                  {levelHeading && <p className="text-xs font-semibold text-gray-700 dark:text-gray-200 pt-1">{levelHeading}</p>}
                  <section className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <p className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wide">Section {idx + 2}</p>
                      <PartyBadge status={party.status} candidateCount={party.candidates?.length ?? 0} sameCompany={party.sameCompany} />
                    </div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{ROLE_LABELS[party.role] || party.role} — {party.customerName}</p>
                    {party.customerAddress && <p className="text-xs text-gray-500 dark:text-gray-400">{party.customerAddress}</p>}

                    {party.status === 'own_account' && (
                      <p className="text-xs text-green-700 dark:text-green-400">
                        ✓ Own BoxTop account <span className="font-mono font-semibold">{party.boxtopAccountNumber}</span> — searched there, not under the Customer's account
                      </p>
                    )}
                    {party.status === 'parent_match' && (party.sameCompany ? (
                      <p className="text-xs text-green-700 dark:text-green-400">
                        ✓ This is the Customer itself ({preflight.invoiceTo.invoiceToCustomerName}) — BoxTop account {party.boxtopAccountNumber}
                      </p>
                    ) : (
                      <p className="text-xs text-blue-700 dark:text-blue-300">
                        A different company from the Customer ({preflight.invoiceTo.invoiceToCustomerName}). It is filed under the Customer's BoxTop account {party.boxtopAccountNumber}, so its address is searched there.
                      </p>
                    ))}
                    {party.status === 'needs_relink' && (
                      <div className="space-y-2">
                        <p className="text-xs text-amber-700 dark:text-amber-400">
                          ⚠ This party has its own BoxTop account ({party.boxtopAccountNumber}) that hasn't been confirmed as belonging under {preflight.invoiceTo.invoiceToCustomerName}'s parent account ({party.parentAccNum}). It may be one of our wrongly-created standalone Contacts — re-link it, or confirm the correct code.
                        </p>
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setDecisions(prev => ({ ...prev, [party.customerId]: { type: 'use', accountNumber: party.parentAccNum! } }))}
                            className={`px-2 py-1 text-xs rounded-md font-medium border ${
                              decision?.type === 'use' && decision.accountNumber === party.parentAccNum
                                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                                : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                            }`}
                          >
                            Use parent account ({party.parentAccNum})
                          </button>
                          <BoxtopCodeEntry label="Or enter a different BoxTop code" onApply={code => setDecisions(prev => ({ ...prev, [party.customerId]: { type: 'use', accountNumber: code } }))} />
                        </div>
                        {decision?.type === 'use' && (
                          <p className="text-xs text-green-700 dark:text-green-400">Will re-link to {decision.accountNumber} on Approve.</p>
                        )}
                      </div>
                    )}
                    {party.status === 'auto_matched' && (
                      <p className="text-xs text-green-700 dark:text-green-400">
                        Matched by name to BoxTop account {party.boxtopAccountNumber} ({party.matchedName}), {party.matchScorePct}% name similarity
                        {party.matchSource && ` — ${MATCH_SOURCE_LABELS[party.matchSource]}`}
                      </p>
                    )}
                    {party.status === 'needs_decision' && (
                      <div className="space-y-2">
                        {(party.candidates || []).map((c, cIdx) => (
                          <label
                            key={`${c.accountNumber}-${cIdx}`}
                            className={`flex items-center justify-between gap-3 p-2.5 rounded-lg border cursor-pointer text-sm ${
                              decision?.type === 'use' && decision.accountNumber === c.accountNumber
                                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                                : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <input
                                type="radio"
                                name={`preflight-match-${party.customerId}`}
                                checked={decision?.type === 'use' && decision.accountNumber === c.accountNumber}
                                onChange={() => {
                                  console.log('[SyncPreflightModal] candidate radio onChange fired:', party.customerId, c.accountNumber)
                                  setDecisions(prev => ({ ...prev, [party.customerId]: { type: 'use', accountNumber: c.accountNumber } }))
                                }}
                              />
                              <div>
                                <p className="font-mono font-semibold text-gray-900 dark:text-white">{c.accountNumber} — {c.name}</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {[c.city, c.country].filter(Boolean).join(', ') || '—'}
                                  {c.source && ` · ${MATCH_SOURCE_LABELS[c.source]}`}
                                </p>
                              </div>
                            </div>
                            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 shrink-0">{c.score}% match</span>
                          </label>
                        ))}
                        {party.level === 'mbl' && (
                          <>
                            <p className="text-xs text-amber-700 dark:text-amber-400">
                              Master-level (MBL) party, independent of the customer: its own BoxTop code is searched first. None was found — pick an account above, enter its code below,
                              or <Link to={`/customers/${party.customerId}`} target="_blank" className="underline">add the code on the company page</Link> and reopen this window.
                            </p>
                            <label className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer text-sm ${
                              decision?.type === 'customer_account_only' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}>
                              <input type="radio" name={`preflight-match-${party.customerId}`} checked={decision?.type === 'customer_account_only'}
                                onChange={() => setDecisions(prev => ({ ...prev, [party.customerId]: { type: 'customer_account_only' } }))} />
                              <span className="text-gray-700 dark:text-gray-300">
                                No code of its own — look for its address in the Customer's account{step1?.accountNumber ? ` (${step1.accountNumber})` : ''} (second round; search only, nothing is created — you confirm what is found below)
                              </span>
                            </label>
                          </>
                        )}
                        {party.level !== 'mbl' && <label
                          className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer text-sm ${
                            decision?.type === 'create_new'
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                              : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                          }`}
                        >
                          <input
                            type="radio"
                            name={`preflight-match-${party.customerId}`}
                            checked={decision?.type === 'create_new'}
                            onChange={() => {
                              console.log('[SyncPreflightModal] "none of these" radio onChange fired:', party.customerId)
                              setDecisions(prev => ({ ...prev, [party.customerId]: { type: 'create_new' } }))
                            }}
                          />
                          <span className="text-gray-700 dark:text-gray-300">None of these — create a new BoxTop Contact</span>
                        </label>}
                        <BoxtopCodeEntry
                          label="Or enter the BoxTop account number directly"
                          onApply={code => setDecisions(prev => ({ ...prev, [party.customerId]: { type: 'use', accountNumber: code } }))}
                        />
                        {decision?.type === 'use' && !(party.candidates || []).some(c => c.accountNumber === decision.accountNumber) && (
                          <p className="text-xs text-green-700 dark:text-green-400">Will link to {decision.accountNumber} on Approve.</p>
                        )}
                      </div>
                    )}
                  </section>
                  </Fragment>
                )
              })}

              <BoxtopAddressLinks
                jobRef={jobRef}
                links={preflight.addressLinks ?? []}
                onChanged={links => setPreflight(p => (p ? { ...p, addressLinks: links } : p))}
              />
              {!addressLinksOk && (
                <p className="text-xs text-amber-600 dark:text-amber-400">An address above still needs to be chosen or created — without it this job cannot be taken into eCustoms in BoxTop.</p>
              )}

              {!allDecided && needsDecisionParties.length > 0 && (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  Still needs a decision: {needsDecisionParties.filter(p => !decisions[p.customerId]).map(p => p.customerName).join(', ')}
                </p>
              )}
              {!invoiceToResolved && (
                <p className="text-xs text-amber-600 dark:text-amber-400">Customer address mismatch still needs resolving above.</p>
              )}
              {approveError && <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg text-sm">{approveError}</div>}
            </>
          )}
        </div>

        <div className="sticky bottom-0 bg-white dark:bg-gray-800 px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex gap-3">
          <button onClick={onCancel} disabled={approving} className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
            Cancel
          </button>
          <button
            onClick={() => {
              // Z58.4: fires on every click regardless of disabled-looking state so a "stuck"
              // click still logs the real state read at that moment, instead of producing zero
              // console output the way a native `disabled` button would.
              console.log('[SyncPreflightModal] Approve All & Sync clicked -- approving:', approving, 'allApproved:', allApproved, 'invoiceToResolved:', invoiceToResolved, 'decisions:', decisions)
              if (approving || !allApproved || !preflight) return
              approve()
            }}
            aria-disabled={approving || !allApproved || !preflight}
            className={`flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium ${
              approving || !allApproved || !preflight ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'
            }`}
          >
            {approving ? 'Saving…' : 'Approve All & Sync'}
          </button>
        </div>
      </div>
    </div>
  )
}
